import { Injectable, inject } from '@angular/core';
import { create } from '@bufbuild/protobuf';
import {
  TimeTrackingServiceAllowedSurveyOptionsRequest,
  TimeTrackingServiceCreateRequest,
  TimeTrackingServiceCreateResponse,
  TimeTrackingServiceCreateResponseSchema,
  TimeTrackingServiceGetResponseSchema,
  TimeTrackingServiceInProgressRequestSchema,
  TimeTrackingServiceListRequest,
  TimeTrackingServiceMarkCompleteRequestSchema,
  TimeTrackingServiceMarkPaidRequestSchema,
  TimeTrackingServiceUpdateRequest,
  TimeTrackingServiceUpdateResponseSchema,
} from '@vmspdb/definitions';
import { ConnectTimeTracking } from '@vmspdb/ui/connect';
import { ApiPaginated } from '@vmspdb/ui/domain';
import { Observable, map } from 'rxjs';
import { ApiRepositoryService } from './api-repository.service';
import { reencode } from './reencode';

@Injectable({
  providedIn: 'root',
})
export class TimeTrackingRepositoryService implements ApiRepositoryService<
  TimeTrackingServiceCreateRequest,
  TimeTrackingServiceUpdateRequest,
  TimeTrackingServiceListRequest,
  TimeTrackingServiceCreateResponse
> {
  private api = inject(ConnectTimeTracking);

  create(
    data: TimeTrackingServiceCreateRequest,
  ): Observable<TimeTrackingServiceCreateResponse> {
    return this.api.create(data).pipe();
  }

  delete(uuid: string): Observable<void> {
    return this.api.delete({ uuid }).pipe(
      map(() => {
        return;
      }),
    );
  }

  fetch(uuid: string): Observable<TimeTrackingServiceCreateResponse> {
    return this.api
      .get({ uuid })
      .pipe(
        map((res) =>
          reencode(
            TimeTrackingServiceGetResponseSchema,
            TimeTrackingServiceCreateResponseSchema,
            res,
          ),
        ),
      );
  }

  list(
    request: TimeTrackingServiceListRequest,
  ): Observable<ApiPaginated<TimeTrackingServiceCreateResponse>> {
    return this.api.list(request);
  }

  update(
    data: TimeTrackingServiceUpdateRequest,
  ): Observable<TimeTrackingServiceCreateResponse> {
    return this.api
      .update(data)
      .pipe(
        map((res) =>
          reencode(
            TimeTrackingServiceUpdateResponseSchema,
            TimeTrackingServiceCreateResponseSchema,
            res,
          ),
        ),
      );
  }

  markPaid(uuids: string[]) {
    return this.api.markPaid(
      create(TimeTrackingServiceMarkPaidRequestSchema, { uuids }),
    );
  }

  markComplete(uuid: string) {
    return this.api.markComplete(
      create(TimeTrackingServiceMarkCompleteRequestSchema, { uuid }),
    );
  }

  inProgress(employeeUuid: string) {
    return this.api.inProgress(
      create(TimeTrackingServiceInProgressRequestSchema, { employeeUuid }),
    );
  }

  allowedSurveyOptions(data: TimeTrackingServiceAllowedSurveyOptionsRequest) {
    return this.api
      .allowedSurveyOptions(data)
      .pipe(
        map(({ options }) =>
          options.map((option) => ({ ...option, value: option.optionValue })),
        ),
      );
  }
}
